/*
 * Copyright Mimic Networks, Inc. 2026.
 */

function parseUtc(iso: string): Date {
  if (iso.endsWith('Z') || /[+-]\d{2}(:?\d{2})?$/.test(iso)) return new Date(iso);
  return new Date(`${iso.replace(' ', 'T')}Z`);
}

/**
 * Format an ISO 8601 timestamp using the user's locale (e.g. "4/26/2026, 6:53:43 PM").
 * Mirrors the legacy telemetry-viewer output so side-by-side comparisons line up.
 */
export function formatTimestamp(iso: string): string {
  return parseUtc(iso).toLocaleString();
}

/**
 * Compact event-count formatting: 1234 → "1.2K", 1_500_000 → "1.5M". Used by
 * the grouped-events table and chart tooltip.
 */
export function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

/**
 * Format a histogram x-axis label. Daily buckets show the date only; smaller
 * buckets show clock time. `bucketLabel` is the BigBird `meta.bucketLabel`
 * value (e.g. "1d", "1h", "15m", "5m", "1m").
 */
export function formatXLabel(timeIso: string, bucketLabel: string): string {
  const date = parseUtc(timeIso);
  if (bucketLabel === '1d') {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
